import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "@/utils/auth";
import { hooks as h } from "@/hooks";
import { ui } from "@patient-watch/ui";
import { Badge } from "@patient-watch/ui/badge";
import { Button } from "@patient-watch/ui/button";
import { Switch } from "@patient-watch/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import {
    type ColumnDef,
    type SortingState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Loader2, Plus } from "lucide-react";
import UnauthorizedPage from "@/components/pages/UnauthorizedPage";
import {
    selectSurgeryOptionsBuilderQueryOptions,
    useSelectSurgeryOptionsForBuilder,
} from "@/features/events/features/surgeries/api/selectSurgeryOptionsForBuilder";
import { useUpdateSurgeryOption } from "@/features/events/features/surgeries/api/updateSurgeryOption";
import type { SurgeryOptionT } from "@patient-watch/types";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import SurgeryOptionForm from "@/features/events/features/surgeries/components/SurgeryOptionForm";

const SurgeriesBuilderPage = () => {
    const auth = useAuth();
    const { mutate: updateSurgeryOption } = useUpdateSurgeryOption();
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    h.useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Surgeries", route: { to: "/builder/surgeries" } },
    ]);

    const { data: surgeryOptions, isLoading } = useSelectSurgeryOptionsForBuilder();

    if (!auth?.roles?.isAdmin && !auth?.isSuperUser) {
        return <UnauthorizedPage message="Admin Only Page" />;
    }

    const [sorting, setSorting] = useState<SortingState>([{ id: "surgery_name", desc: false }]);

    const columns: ColumnDef<SurgeryOptionT>[] = useMemo(
        () => [
            {
                accessorKey: "surgery_name",
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Surgery Name
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
            },
            {
                accessorKey: "orthopaedic_area",
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Anatomic Region
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
                cell: ({ row }) => (
                    <span className="text-muted-foreground">{row.original.orthopaedic_area ?? "—"}</span>
                ),
            },
            {
                accessorKey: "is_public",
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Published
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
                cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={row.original.is_public}
                            onCheckedChange={(checked) =>
                                updateSurgeryOption({
                                    surgeryOptionId: row.original.surgery_option_id,
                                    updates: { is_public: checked === true },
                                })
                            }
                        />
                        <Badge variant={row.original.is_public ? "default" : "secondary"} className="text-xs">
                            {row.original.is_public ? "Yes" : "No"}
                        </Badge>
                    </div>
                ),
            },
        ],
        [updateSurgeryOption]
    );

    const table = useReactTable({
        data: surgeryOptions ?? [],
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-2 md:p-4">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="font-semibold text-3xl tracking-tight">Surgery Options</h1>
                    <p className="text-muted-foreground">
                        Manage surgery options available in diaries. Toggle Published to make options visible to all
                        clinicians.
                    </p>
                </div>
                <ui.Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                    <ui.DialogTrigger asChild>
                        <Button size="sm">
                            <Plus className="mr-2 w-4 h-4" />
                            Add Surgery Option
                        </Button>
                    </ui.DialogTrigger>
                    <ui.DialogContent>
                        <ui.DialogHeader>
                            <ui.DialogTitle>Add Surgery Option</ui.DialogTitle>
                            <ui.DialogDescription>
                                Create a new surgery option. Admins can publish it to make it available to all
                                clinicians.
                            </ui.DialogDescription>
                        </ui.DialogHeader>
                        <SurgeryOptionForm onSuccess={() => setAddDialogOpen(false)} />
                    </ui.DialogContent>
                </ui.Dialog>
            </div>

            <ui.Card>
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className={cn(
                                            header.column.getCanSort() &&
                                                "cursor-pointer select-none hover:bg-slate-50 dark:hover:bg-slate-800"
                                        )}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="py-12 text-muted-foreground text-center">
                                    No surgery options found. Add one to get started.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </ui.Card>
        </div>
    );
};

export const Route = createFileRoute("/_app/builder/surgeries/")({
    component: SurgeriesBuilderPage,
    loader: async ({ context }) => {
        await context.queryClient.ensureQueryData(selectSurgeryOptionsBuilderQueryOptions());
    },
});
